import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Modal, Input, Select, Field, Spinner } from "./ui";
import { cn } from "../lib/utils";
import { Plus, KanbanSquare, Table2, Settings2, Trash2, X, Building2, User, CalendarClock, Euro, ArrowUp, ArrowDown, GripVertical, Search, FileText, ChevronDown, Hourglass, Briefcase, Tag, Target } from "lucide-react";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Lightweight searchable single-select used for company/project pickers.
function SearchSelect({ value, options, onChange, placeholder, testid, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const sel = options.find((o) => o.id === value);
  const filtered = options.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()));
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="relative" data-testid={testid} ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-trigger`}
        className="w-full flex items-center justify-between rounded-lg border bg-surface px-3 py-2 text-sm text-left hover:border-primary/50 transition-colors">
        <span className={cn("truncate", !sel && "text-text-secondary")}>{sel ? sel.label : (placeholder || "—")}</span>
        <ChevronDown className="w-4 h-4 text-text-secondary shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border bg-surface shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b">
            <Search className="w-3.5 h-3.5 text-text-secondary" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
              data-testid={`${testid}-search`} className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {allowClear && <button type="button" onClick={() => { onChange(""); setOpen(false); setQ(""); }} className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover">—</button>}
            {filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQ(""); }}
                data-testid={`${testid}-opt-${o.id}`}
                className={cn("w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover truncate", o.id === value && "text-primary font-medium")}>{o.label}</button>
            ))}
            {!filtered.length && <p className="px-3 py-2 text-xs text-text-secondary">—</p>}
          </div>
        </div>
      )}
    </div>
  );
}

const tint = (hex) => { const h = (hex || "#64748B").replace("#", ""); return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},0.12)`; };
const fmtMoney = (v, cur, loc) => (v == null || v === "" ? null : new Intl.NumberFormat(loc, { style: "currency", currency: cur || "EUR", maximumFractionDigits: 0 }).format(v));
const fmtDate = (s, loc) => { if (!s) return null; const d = new Date(`${s}T00:00:00`); return isNaN(d.getTime()) ? null : d.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" }); };

// Deal aging: days since the deal last changed stage (falls back to created_at).
// >30d => stale (red), >14d => aging (amber). Closed (won/lost) stages never age.
const AGING_DAYS = 14, STALE_DAYS = 30;
function dealAging(deal, stageType) {
  if (stageType === "won" || stageType === "lost") return null;
  const src = deal.stage_changed_at || deal.created_at;
  if (!src) return null;
  const t = new Date(src).getTime();
  if (isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < AGING_DAYS) return null;
  return { days, level: days >= STALE_DAYS ? "stale" : "aging" };
}

function DealCard({ deal, stageType, companyName, ownerName, projectName, personNames, krName, onKrClick, loc, onOpen }) {
  const { t } = useApp();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deal.id });
  const money = fmtMoney(deal.value, deal.currency, loc);
  const due = fmtDate(deal.expected_close, loc);
  const aging = dealAging(deal, stageType);
  const project = projectName ? projectName(deal.project_id) : null;
  const persons = personNames ? personNames(deal.people_ids) : [];
  const personLabel = persons.length ? (persons.length > 1 ? `${persons[0]} +${persons.length - 1}` : persons[0]) : null;
  const kr = krName ? krName(deal.key_result_id) : null;
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes} {...listeners}
      data-testid={`deal-card-${deal.id}`}
      className={cn("relative bg-surface p-3.5 rounded-xl border cursor-grab active:cursor-grabbing transition-all group/dc",
        isDragging ? "opacity-30" : "hover:shadow-lg hover:-translate-y-0.5",
        aging?.level === "stale" && "border-red-500/40")}>
      <button onClick={(e) => { e.stopPropagation(); onOpen(deal); }} onPointerDown={(e) => e.stopPropagation()} data-testid={`deal-open-${deal.id}`}
        className="absolute top-2.5 right-2.5 opacity-0 group-hover/dc:opacity-100 text-text-secondary hover:text-text-primary transition-all"><Settings2 className="w-3.5 h-3.5" /></button>
      <p className="text-sm font-semibold leading-snug break-words pr-5">{deal.title}</p>
      {companyName && <p className="text-xs text-text-secondary mt-1 flex items-center gap-1"><Building2 className="w-3 h-3 shrink-0" />{companyName}</p>}
      {personLabel && <p className="text-xs text-text-secondary mt-1 flex items-center gap-1" data-testid={`deal-card-person-${deal.id}`}><User className="w-3 h-3 shrink-0" />{personLabel}</p>}
      {project && <p className="text-xs text-text-secondary mt-1 flex items-center gap-1" data-testid={`deal-card-project-${deal.id}`}><Briefcase className="w-3 h-3 shrink-0" />{project}</p>}
      {kr && (
        <button onClick={(e) => { e.stopPropagation(); onKrClick && onKrClick(); }} onPointerDown={(e) => e.stopPropagation()} data-testid={`deal-kr-${deal.id}`}
          className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-500/15 text-violet-500 hover:bg-violet-500/25 transition-colors max-w-full">
          <Target className="w-3 h-3 shrink-0" /><span className="truncate">KR: {kr}</span>
        </button>
      )}
      <div className="flex items-center gap-3 mt-2.5 flex-wrap">
        {money && <span className="text-xs font-semibold text-emerald-500">{money}</span>}
        {deal.probability != null && <span className="text-[11px] text-text-secondary">{deal.probability}%</span>}
        {due && <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary"><CalendarClock className="w-3 h-3" />{due}</span>}
        <div className="flex-1" />
        {ownerName && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-hover text-text-secondary">{ownerName}</span>}
      </div>
      {(aging || deal.source) && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {aging && (
            <span data-testid={`deal-aging-${deal.id}`} title={t("deal_aging_tooltip").replace("{days}", aging.days)}
              className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                aging.level === "stale" ? "bg-red-500/15 text-red-500" : "bg-amber-500/15 text-amber-500")}>
              <Hourglass className="w-3 h-3" />
              {(aging.level === "stale" ? t("deal_aging_stale") : t("deal_aging_label")).replace("{days}", aging.days)}
            </span>
          )}
          {deal.source && (
            <span data-testid={`deal-card-source-${deal.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-hover text-text-secondary">
              <Tag className="w-3 h-3" />{deal.source}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function StageColumn({ stage, deals, sum, companyName, ownerName, projectName, personNames, krName, onKrClick, loc, onOpen, onAdd }) {
  const { t } = useApp();
  const { setNodeRef } = useDroppable({ id: stage.id });
  return (
    <div className="w-[260px] shrink-0 flex flex-col max-h-full" data-testid={`pipeline-col-${stage.id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ backgroundColor: tint(stage.color) }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
        <span className="text-sm font-bold tracking-tight truncate flex-1">{stage.name}</span>
        <span className="text-[11px] font-bold tabular-nums text-text-secondary">{deals.length}</span>
        <button onClick={() => onAdd(stage)} data-testid={`pipeline-add-${stage.id}`} className="text-text-secondary hover:text-text-primary"><Plus className="w-4 h-4" /></button>
      </div>
      {sum > 0 && <div className="text-[11px] text-text-secondary px-3 mt-1.5">{fmtMoney(sum, "EUR", loc)}</div>}
      <div ref={setNodeRef} className="flex-1 overflow-y-auto mt-2 p-1 space-y-2.5">
        <SortableContext items={deals.map((d) => d.id)} strategy={verticalListSortingStrategy}>
          {deals.map((d) => <DealCard key={d.id} deal={d} stageType={stage.type} companyName={companyName(d.company_id)} ownerName={ownerName(d.owner_id)} projectName={projectName} personNames={personNames} krName={krName} onKrClick={onKrClick} loc={loc} onOpen={onOpen} />)}
        </SortableContext>
        {deals.length === 0 && <div className="h-14 rounded-xl border-2 border-dashed flex items-center justify-center text-[11px] text-text-secondary/50">—</div>}
      </div>
    </div>
  );
}

export default function DealsPipeline({ entity }) {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [data, setData] = useState(null);
  const [view, setView] = useState("kanban");
  const [companies, setCompanies] = useState([]);
  const [people, setPeople] = useState([]);
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showStages, setShowStages] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [cols, setCols] = useState({});
  const [projects, setProjects] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [winPrompt, setWinPrompt] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    const { data } = await api.get(`/entities/${entity.id}/pipeline`);
    setData(data);
  }, [entity.id]);

  useEffect(() => {
    load();
    api.get("/data/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    api.get("/data/contacts").then(({ data }) => setPeople(data)).catch(() => {});
    api.get(`/entity-members?entity_id=${entity.id}`).then(({ data }) => setMembers(data)).catch(() => {});
    // Real projects live in Spaces (space_projects); the legacy generic table is retired.
    api.get(`/space-projects?entity_id=${entity.id}`).then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    // Key Results for strategic alignment (deal ↔ OKR).
    api.get(`/okr/board?entity_id=${entity.id}`).then(({ data }) => setKeyResults(data.key_results || [])).catch(() => setKeyResults([]));
  }, [load, entity.id]);

  const companyName = useCallback((id) => companies.find((c) => c.id === id)?.name || null, [companies]);
  const ownerName = useCallback((id) => { const m = members.find((x) => x.user_id === id); return m ? (m.name || m.email) : null; }, [members]);
  const projectName = useCallback((id) => projects.find((p) => p.id === id)?.name || null, [projects]);
  const personNames = useCallback((ids) => (ids || []).map((id) => people.find((p) => p.id === id)?.name).filter(Boolean), [people]);
  const krName = useCallback((id) => keyResults.find((k) => k.id === id)?.title || null, [keyResults]);
  const goToOkr = useCallback(() => navigate(`/entity/${entity.key}?tab=okr`), [navigate, entity.key]);

  const stages = useMemo(() => data?.stages || [], [data]);
  const deals = useMemo(() => data?.deals || [], [data]);
  const firstStageId = stages[0]?.id;

  useEffect(() => {
    if (activeId) return;
    const map = {}; stages.forEach((s) => { map[s.id] = []; });
    [...deals].sort((a, b) => (a.position || 0) - (b.position || 0)).forEach((d) => { (map[d.stage_id] || map[firstStageId])?.push(d.id); });
    setCols(map);
  }, [data, activeId, stages, deals, firstStageId]);

  const dealsById = useMemo(() => Object.fromEntries(deals.map((d) => [d.id, d])), [deals]);
  const findCol = (id) => (cols[id] ? id : Object.keys(cols).find((k) => cols[k]?.includes(id)));

  // after a deal becomes won, offer to create a draft invoice (unless one is already linked)
  const maybeOfferInvoice = (deal, status) => {
    if (status === "won" && deal && !deal.invoice_id && deal.company_id) setWinPrompt(deal);
  };
  const createInvoice = async (deal) => {
    const { data } = await api.post(`/crm/deals/${deal.id}/invoice`);
    setWinPrompt(null);
    load();
    return data?.invoice;
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const ac = findCol(active.id), oc = findCol(over.id);
    if (!ac || !oc || ac === oc) return;
    setCols((prev) => {
      const a = [...(prev[ac] || [])], o = [...(prev[oc] || [])];
      const ai = a.indexOf(active.id); if (ai < 0) return prev;
      let oi = o.indexOf(over.id); if (oi < 0) oi = o.length;
      a.splice(ai, 1); o.splice(oi, 0, active.id);
      return { ...prev, [ac]: a, [oc]: o };
    });
  };
  const onDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const oc = findCol(over.id); if (!oc) return;
    const items = [...(cols[oc] || [])];
    const ai = items.indexOf(active.id); if (ai < 0) return;
    let oi = items.indexOf(over.id); if (over.id === oc || oi < 0) oi = items.length - 1;
    const next = { ...cols, [oc]: arrayMove(items, ai, oi) };
    setCols(next);
    const pos = next[oc].indexOf(active.id);
    const { data } = await api.put(`/crm/deals/${active.id}/move`, { stage_id: oc, position: pos });
    maybeOfferInvoice(dealsById[active.id], data?.status);
    load();
  };

  const saveDeal = async (form) => {
    let saved;
    if (editing?.id) { const r = await api.put(`/crm/deals/${editing.id}`, form); saved = r.data; }
    else { const r = await api.post(`/entities/${entity.id}/deals`, form); saved = r.data; }
    setEditing(null);
    maybeOfferInvoice(saved, saved?.status);
    load();
  };
  const deleteDeal = async (id) => { await api.delete(`/crm/deals/${id}`); setEditing(null); load(); };

  if (!data) return <div className="py-16 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>;

  const totalOpen = deals.filter((d) => d.status === "open").reduce((a, d) => a + (d.value || 0), 0);
  const won = deals.filter((d) => d.status === "won").reduce((a, d) => a + (d.value || 0), 0);
  const activeDeal = activeId ? dealsById[activeId] : null;
  const collision = (args) => { const p = pointerWithin(args); return p.length ? p : rectIntersection(args); };

  return (
    <div data-testid="deals-pipeline">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-text-secondary">{t("deals_open_value")} <span className="font-semibold text-text-primary">{fmtMoney(totalOpen, "EUR", loc) || "—"}</span></span>
          <span className="text-text-secondary">{t("deals_won_value")} <span className="font-semibold text-emerald-500">{fmtMoney(won, "EUR", loc) || "—"}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 p-0.5 rounded-lg border bg-surface">
            <button onClick={() => setView("kanban")} data-testid="deals-view-kanban" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs", view === "kanban" ? "bg-text-primary text-background" : "text-text-secondary")}><KanbanSquare className="w-3.5 h-3.5" />{t("deals_kanban")}</button>
            <button onClick={() => setView("table")} data-testid="deals-view-table" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs", view === "table" ? "bg-text-primary text-background" : "text-text-secondary")}><Table2 className="w-3.5 h-3.5" />{t("deals_table")}</button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowStages(true)} data-testid="deals-manage-stages"><Settings2 className="w-3.5 h-3.5" />{t("deals_stages")}</Button>
          <Button size="sm" onClick={() => setEditing({ stage_id: stages[0]?.id })} data-testid="deals-new"><Plus className="w-3.5 h-3.5" />{t("deals_new")}</Button>
        </div>
      </div>

      {view === "kanban" ? (
        <DndContext sensors={sensors} collisionDetection={collision} onDragStart={(e) => setActiveId(e.active.id)} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
          <div className="flex gap-3 items-start overflow-x-auto pb-2" style={{ maxHeight: "calc(100vh - 18rem)" }}>
            {stages.map((st) => {
              const ids = cols[st.id] || [];
              const stDeals = ids.map((id) => dealsById[id]).filter(Boolean);
              const sum = stDeals.reduce((a, d) => a + (d.value || 0), 0);
              return <StageColumn key={st.id} stage={st} deals={stDeals} sum={sum} companyName={companyName} ownerName={ownerName} projectName={projectName} personNames={personNames} krName={krName} onKrClick={goToOkr} loc={loc} onOpen={setEditing} onAdd={(s) => setEditing({ stage_id: s.id })} />;
            })}
          </div>
          <DragOverlay>{activeDeal ? <div className="w-[250px]"><div className="bg-surface p-3.5 rounded-xl border shadow-2xl rotate-2"><p className="text-sm font-semibold">{activeDeal.title}</p></div></div> : null}</DragOverlay>
        </DndContext>
      ) : (
        <DealsTable deals={deals} stages={stages} companyName={companyName} ownerName={ownerName} loc={loc} onOpen={setEditing} />
      )}

      {editing && <DealEditor deal={editing} stages={stages} companies={companies} people={people} members={members} projects={projects} keyResults={keyResults}
        onClose={() => setEditing(null)} onSave={saveDeal} onDelete={deleteDeal} onCreateInvoice={createInvoice} />}
      {showStages && <StageManager entity={entity} stages={stages} onClose={() => setShowStages(false)} onChanged={load} />}
      {winPrompt && (
        <Modal open onClose={() => setWinPrompt(null)} title={t("deal_won_title")}
          footer={<>
            <Button variant="outline" onClick={() => setWinPrompt(null)} data-testid="win-skip">{t("deal_won_skip")}</Button>
            <Button onClick={() => createInvoice(winPrompt)} data-testid="win-create-invoice">{t("deal_won_create")}</Button>
          </>}>
          <p className="text-sm text-text-secondary" data-testid="win-prompt">
            {t("deal_won_body").replace("{title}", winPrompt.title).replace("{value}", fmtMoney(winPrompt.value, winPrompt.currency, loc) || "—")}
          </p>
        </Modal>
      )}
    </div>
  );
}

function DealsTable({ deals, stages, companyName, ownerName, loc, onOpen }) {
  const { t } = useApp();
  const stageName = (id) => stages.find((s) => s.id === id);
  if (!deals.length) return <div className="rounded-2xl border border-dashed p-12 text-center text-sm text-text-secondary" data-testid="deals-table-empty">{t("deals_empty")}</div>;
  return (
    <div className="rounded-2xl border overflow-hidden" data-testid="deals-table">
      <table className="w-full text-sm">
        <thead className="bg-surface text-text-secondary text-xs">
          <tr>
            <th className="text-left font-medium px-4 py-2.5">{t("deals_col_title")}</th>
            <th className="text-left font-medium px-4 py-2.5">{t("deals_col_company")}</th>
            <th className="text-left font-medium px-4 py-2.5">{t("deals_col_stage")}</th>
            <th className="text-right font-medium px-4 py-2.5">{t("deals_col_value")}</th>
            <th className="text-right font-medium px-4 py-2.5">%</th>
            <th className="text-left font-medium px-4 py-2.5">{t("deals_col_close")}</th>
            <th className="text-left font-medium px-4 py-2.5">{t("deals_col_owner")}</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => { const st = stageName(d.stage_id); const aging = dealAging(d, st?.type); return (
            <tr key={d.id} onClick={() => onOpen(d)} data-testid={`deal-row-${d.id}`} className="border-t hover:bg-surface-hover cursor-pointer">
              <td className="px-4 py-2.5 font-medium">
                <span className="inline-flex items-center gap-2">{d.title}
                  {aging && <span data-testid={`deal-row-aging-${d.id}`} title={t("deal_aging_tooltip").replace("{days}", aging.days)}
                    className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                      aging.level === "stale" ? "bg-red-500/15 text-red-500" : "bg-amber-500/15 text-amber-500")}>
                    <Hourglass className="w-3 h-3" />{aging.days}{t("deal_aging_days_short")}</span>}
                </span>
              </td>
              <td className="px-4 py-2.5 text-text-secondary">{companyName(d.company_id) || "—"}</td>
              <td className="px-4 py-2.5">{st && <span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: st.color }} />{st.name}</span>}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(d.value, d.currency, loc) || "—"}</td>
              <td className="px-4 py-2.5 text-right text-text-secondary">{d.probability != null ? `${d.probability}%` : "—"}</td>
              <td className="px-4 py-2.5 text-text-secondary">{fmtDate(d.expected_close, loc) || "—"}</td>
              <td className="px-4 py-2.5 text-text-secondary">{ownerName(d.owner_id) || "—"}</td>
            </tr>
          ); })}
        </tbody>
      </table>
    </div>
  );
}

function DealEditor({ deal, stages, companies, people, members, projects, keyResults, onClose, onSave, onDelete, onCreateInvoice }) {
  const { t, lang } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [f, setF] = useState({
    title: deal.title || "", stage_id: deal.stage_id || stages[0]?.id, value: deal.value ?? "",
    currency: deal.currency || "EUR", probability: deal.probability ?? "", expected_close: deal.expected_close || "",
    source: deal.source || "", company_id: deal.company_id || "", people_ids: deal.people_ids || [], owner_id: deal.owner_id || "",
    project_id: deal.project_id || "", key_result_id: deal.key_result_id || "",
  });
  const [acts, setActs] = useState(null);
  const [note, setNote] = useState("");
  const [invoice, setInvoice] = useState(null);
  const wonStage = stages.find((s) => s.id === f.stage_id)?.type === "won";
  useEffect(() => {
    if (deal.id) api.get(`/crm/deals/${deal.id}/activities`).then(({ data }) => setActs(data)).catch(() => setActs([]));
    if (deal.invoice_id) api.get(`/data/invoices?entity_id=${deal.entity_id}`).then(({ data }) => setInvoice((data || []).find((i) => i.id === deal.invoice_id) || null)).catch(() => {});
  }, [deal.id, deal.invoice_id, deal.entity_id]);
  const postNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/crm/deals/${deal.id}/activities`, { text: note.trim() });
    setActs(data); setNote("");
  };
  const makeInvoice = async () => {
    const inv = await onCreateInvoice(deal);
    if (inv) setInvoice(inv);
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const togglePerson = (id) => set("people_ids", f.people_ids.includes(id) ? f.people_ids.filter((x) => x !== id) : [...f.people_ids, id]);
  // people are filtered to the selected company (the rolodex is shared; a deal's contacts come from its company)
  const companyPeople = f.company_id ? people.filter((p) => p.company_id === f.company_id) : people;
  const submit = () => {
    if (!f.title.trim()) return;
    onSave({ ...f, value: f.value === "" ? null : Number(f.value), probability: f.probability === "" ? null : Number(f.probability) });
  };
  const actLabel = (a) => a.kind === "created" ? t("deal_act_created") : a.kind === "stage_change" ? `${t("deal_act_stage")}: ${a.text}` : a.text;
  return (
    <Modal open onClose={onClose} title={deal.id ? t("deals_edit") : t("deals_new")}
      footer={<>
        {deal.id && <button onClick={() => onDelete(deal.id)} data-testid="deal-delete" className="text-sm text-red-400 hover:text-red-300 mr-auto inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" />{t("delete")}</button>}
        <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={submit} disabled={!f.title.trim()} data-testid="deal-save">{t("save")}</Button>
      </>}>
      <div className="space-y-4">
        <Field label={t("deals_col_title")}><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="deal-title" autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("deals_col_stage")}><Select value={f.stage_id} onChange={(e) => set("stage_id", e.target.value)} data-testid="deal-stage">{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          <Field label={t("deals_col_owner")}><Select value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} data-testid="deal-owner"><option value="">—</option>{members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}</Select></Field>
          <Field label={t("deals_col_value")}><Input type="number" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="deal-value" /></Field>
          <Field label={t("deals_probability")}><Input type="number" min="0" max="100" value={f.probability} onChange={(e) => set("probability", e.target.value)} data-testid="deal-prob" /></Field>
          <Field label={t("deals_col_close")}><Input type="date" value={(f.expected_close || "").slice(0, 10)} onChange={(e) => set("expected_close", e.target.value)} data-testid="deal-close" /></Field>
          <Field label={t("deals_source")}><Input value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="deal-source" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("deals_col_company")}>
            <SearchSelect value={f.company_id} options={companies.map((c) => ({ id: c.id, label: c.name }))}
              onChange={(id) => set("company_id", id)} placeholder={t("deals_search_company")} testid="deal-company" />
          </Field>
          <Field label={t("deals_col_project")}>
            <SearchSelect value={f.project_id} options={projects.map((p) => ({ id: p.id, label: p.name }))}
              onChange={(id) => set("project_id", id)} placeholder={t("deals_search_project")} testid="deal-project" />
          </Field>
        </div>
        <Field label={t("deals_kr")}>
          <SearchSelect value={f.key_result_id} options={(keyResults || []).map((k) => ({ id: k.id, label: k.title }))}
            onChange={(id) => set("key_result_id", id)} placeholder={t("deals_search_kr")} testid="deal-kr" />
        </Field>
        <div>
          <div className="text-xs text-text-secondary mb-1.5">{t("deals_contacts")}{f.company_id && <span className="text-text-secondary/60"> · {t("deals_people_filtered")}</span>}</div>
          <div className="flex flex-wrap gap-1.5" data-testid="deal-people">
            {companyPeople.map((p) => <button key={p.id} onClick={() => togglePerson(p.id)} className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs", f.people_ids.includes(p.id) ? "border-primary bg-primary/10 text-text-primary" : "text-text-secondary")}><User className="w-3 h-3" />{p.name}</button>)}
            {!companyPeople.length && <span className="text-xs text-text-secondary">{f.company_id ? t("deals_no_company_people") : `${t("crm_people")}: —`}</span>}
          </div>
        </div>

        {deal.id && (
          <div className="pt-1" data-testid="deal-invoice-section">
            {invoice ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-surface text-sm" data-testid="deal-invoice-linked">
                <FileText className="w-4 h-4 text-emerald-500" />
                <span>{t("deals_invoice")}: <span className="font-medium">{invoice.number}</span></span>
                <span className="text-text-secondary tabular-nums ml-auto">{fmtMoney(invoice.amount, "EUR", loc)}</span>
              </div>
            ) : wonStage ? (
              <Button variant="outline" className="w-full" onClick={makeInvoice} data-testid="deal-create-invoice"><FileText className="w-4 h-4 mr-1.5" />{t("deal_won_create")}</Button>
            ) : null}
          </div>
        )}

        {deal.id && (
          <div className="pt-2 border-t">
            <div className="text-xs font-medium text-text-secondary mb-2">{t("deal_activity")}</div>
            <div className="flex gap-2 mb-3">
              <Input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postNote()} placeholder={t("deal_add_note")} data-testid="deal-note-input" />
              <Button variant="outline" onClick={postNote} disabled={!note.trim()} data-testid="deal-note-add"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="deal-activity-list">
              {acts === null ? <Spinner className="w-4 h-4 text-text-secondary" /> :
                acts.length === 0 ? <p className="text-xs text-text-secondary">{t("deal_no_activity")}</p> :
                acts.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 text-xs" data-testid={`deal-activity-${a.id}`}>
                    <span className={cn("mt-1 w-1.5 h-1.5 rounded-full shrink-0", a.kind === "note" ? "bg-sky-400" : a.kind === "stage_change" ? "bg-violet-400" : "bg-text-secondary")} />
                    <div className="min-w-0 flex-1">
                      <p className="text-text-primary break-words">{actLabel(a)}</p>
                      <p className="text-[10px] text-text-secondary">{a.actor_name} · {fmtDate(a.created_at, loc) || new Date(a.created_at).toLocaleDateString(loc)}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function StageManager({ entity, stages, onClose, onChanged }) {
  const { t } = useApp();
  const [list, setList] = useState(stages);
  const [adding, setAdding] = useState("");
  const refresh = async () => { const { data } = await api.get(`/entities/${entity.id}/pipeline`); setList(data.stages); onChanged(); };
  const add = async () => { if (!adding.trim()) return; await api.post(`/entities/${entity.id}/stages`, { name: adding.trim(), type: "open", color: "#64748B" }); setAdding(""); refresh(); };
  const update = async (s, patch) => { await api.put(`/crm/stages/${s.id}`, { name: s.name, type: s.type, color: s.color, ...patch }); refresh(); };
  const del = async (s) => { await api.delete(`/crm/stages/${s.id}`); refresh(); };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    const ids = list.map((s) => s.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.put(`/entities/${entity.id}/stages/reorder`, { stage_ids: ids }); refresh();
  };
  return (
    <Modal open onClose={onClose} title={t("deals_manage_stages")} footer={<Button onClick={onClose}>{t("done") || "Done"}</Button>}>
      <div className="space-y-2" data-testid="stage-manager">
        {list.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border" data-testid={`stage-row-${s.id}`}>
            <div className="flex flex-col"><button onClick={() => move(i, -1)} disabled={i === 0} className="text-text-secondary disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button><button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="text-text-secondary disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button></div>
            <input type="color" value={s.color} onChange={(e) => update(s, { color: e.target.value })} className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent" data-testid={`stage-color-${s.id}`} />
            <Input value={s.name} onChange={(e) => setList((p) => p.map((x) => x.id === s.id ? { ...x, name: e.target.value } : x))} onBlur={() => update(s, {})} className="flex-1" data-testid={`stage-name-${s.id}`} />
            <Select value={s.type} onChange={(e) => update(s, { type: e.target.value })} className="max-w-[110px]" data-testid={`stage-type-${s.id}`}>
              <option value="open">{t("stage_open")}</option><option value="won">{t("stage_won")}</option><option value="lost">{t("stage_lost")}</option>
            </Select>
            <button onClick={() => del(s)} disabled={list.length <= 1} data-testid={`stage-delete-${s.id}`} className="text-text-secondary hover:text-red-400 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2">
          <Input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={t("deals_add_stage")} data-testid="stage-add-input" />
          <Button variant="outline" onClick={add} data-testid="stage-add"><Plus className="w-4 h-4" /></Button>
        </div>
      </div>
    </Modal>
  );
}
